import { Location, PopStateEvent } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import pick from 'lodash/pick';
import { Observable, Subject, Subscription, combineLatest, defer, forkJoin, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilKeyChanged, filter, first, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { ConfigService } from '../config.service';
import { ContainerAttachmentsService, FileIdentifier } from '../containers/attachments/container-attachments.service';
import { CoreApiService } from '../core-api.service';
import { DialogResult, ViewerFrameDialog } from './frame/viewer-frame-dialog.component';
import { ViewerTextConfig } from './frame/viewer-frame.model';
import { HTMLViewerDialog } from './html-viewer/html-viewer-dialog.component';
import { LegacyOhifViewerDialog } from './legacy-ohif-viewer/legacy-ohif-viewer-dialog.component';
import { LoggingService } from '../logging.service';
import { MarkdownService } from './markdown.service';
import { NotificationService } from '../notification.service';
import { OhifViewerDialog } from './ohifviewer/ohifviewer-dialog.component';
import { OhifApp, OhifLaunchConfig, buildUrl as buildOhifUrl } from './ohifviewer/ohifviewer.utils';
import { PDFViewerDialog } from './pdf-viewer/pdf-viewer-dialog.component';
import { ProfileService } from '../profile/profile.service';
import { getContainerProjectId, getContainerType } from '../shared/container.utils';
import { Container } from '../shared/models/container.model';
import { File } from '../shared/models/file.model';
import { AccessPermission } from '../shared/models/permission.model';
import { ViewerApp } from '../shared/models/site-settings.model';
import { firstValueFrom } from '../shared/observable.utils';
import { deconstructUrl, objectToUrlParam, urlParamToObject } from '../shared/url.utils';
import { TextViewerDialog } from './text-viewer/text-viewer-dialog.component';
import { FileType, getLastExtension, getViewerType } from './types';
import { VideoViewerDialog } from './video-viewer/video-viewer-dialog.component';
import { findAssociatedApps } from './viewer.utils';
import defaultViewerApps from '../../viewers.json';
import { RoutingHelperService } from '../routing-helper.service';
import { DialogService } from '../dialogs';
import { Protocol } from '../shared/models/protocol.model';

// these match the internal viewer URLs in the viewers.json
enum InternalViewerUrl {
  OhifMeteor = 'internal:ohif_meteor', // legacy viewer
  OhifReact = 'internal:ohif_react', // v2
  OhifViewerV3 = 'internal:ohif_viewer_v3', // v3
}
interface AppCriteria {
  container?: Container
  file?: File
  projectId?: string
}

interface ViewerLaunchConfig {
  app?: ViewerApp
  container?: Container
  file?: File
  ohif?: OhifLaunchConfig
  params?: {
    fileIds?: string[]
    // honestly unsure if other keys are being used here, only named params are supported
    [key: string]: string | string[]
  }
}

interface ViewerConfig extends ViewerLaunchConfig {
  text?: string
}

export interface ProtocolViewerConfig {
  hasSaveableProtocol: boolean
  hasDraft: boolean
}

interface ViewerOptions extends ViewerConfig {
  attachmentUrl?: string
  requestConfig: RequestConfig
  viewer?: Function
}

// Dialogs should be able to return any type of object
type ViewerResult = Observable<any> // eslint-disable-line @typescript-eslint/no-explicit-any
// Callback type for a string resource
type SaveObservableCallback = (value: string, dialog?: MatDialogRef<any, any>) => Observable<any> // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * This interface is a function that returns the afterClose Observable result from the dialog.
 */
interface ViewerFunction {
  (config: ViewerOptions): ViewerResult
}

interface RequestConfig {
  container: string,
  params: Record<string, string>,
}

// https://stackoverflow.com/a/3561711
const escapeRegex = (s: string): string => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

function getFileIdentifier(container: Container, file: File): FileIdentifier {
  return { fileId: file.file_id, version: file.version, containerId: container._id, fileName: file.name };
}

// This service NEEDS to be a singleton, so it is provided in the root injector. Do NOT provide it in any module or component.
@Injectable({
  providedIn: 'root',
})
export class ViewerService {

  readonly projectViewerApps: Map<string, Observable<ViewerApp[]>> = new Map(); // projectId -> Observable<ViewerApp[]>
  readonly defaultViewerApps$ = of(defaultViewerApps).pipe(
    map(defaultApps => this.configService.isFeatureEnabled('default_viewer_apps') ? defaultApps : []),
    map(defaultApps => defaultApps.filter(app => this.filterEnabledApps(app))),
  )
  readonly viewerApps$ = defer(() => this.profileService.getProfile()).pipe(
    // make sure we are logged in and refresh this stream whenever the user changes
    filter(data => Boolean(data)),
    switchMap(() => combineLatest([
      this.defaultViewerApps$,
      this.configService.getSiteSettings({ refresh: true }),
    ])),
    map(([defaultApps, siteSettings]) => {
      this.resetProjectApps();
      const viewers = (siteSettings.viewer_apps || []).concat(defaultApps).filter(app => this.filterEnabledApps(app));
      return this.renameApplications(viewers);
    }),
    shareReplay(1),
  );

  private persistedViewerOpen = false;
  private subscriptions = {
    route: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    private containerAttachments: ContainerAttachmentsService,
    private dialogService: DialogService,
    private coreApi: CoreApiService,
    private location: Location,
    private logger: LoggingService,
    private markdownService: MarkdownService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private activatedRoute: ActivatedRoute,
    private routingService: RoutingHelperService,
  ) {
    this.subscriptions.route = this.activatedRoute.queryParams.subscribe({
      next: (qParams) => {
        const { viewerParams } = qParams;
        // only restore the viewer state if the viewerParams are set and the viewer was not opened by the viewer service
        if (viewerParams && !this.persistedViewerOpen) {
          this.restoreViewerState(viewerParams);
        } else if (!viewerParams) {
          // viewers should clear viewerParams after closing, triggering this and allowing users to open viewers from a link again
          this.persistedViewerOpen = false;
        }
      },
    });
  }

  public getAssociatedApps(options: AppCriteria): Observable<ViewerApp[]> {
    return this.getAllApps(options).pipe(
      first(),
      map(apps => findAssociatedApps(apps, options)),
    );
  }

  private getAllApps(options: AppCriteria): Observable<ViewerApp[]> {
    // returns [...projectApps, ...siteApps] as applicable without filtering based on container/permissions
    let projectApps$: Observable<ViewerApp[]> = of([]);
    if (options.projectId && this.configService.isFeatureEnabled('core_api')) {
      if (this.projectViewerApps.has(options.projectId)) {
        projectApps$ = this.projectViewerApps.get(options.projectId);
      } else {
        projectApps$ = this.coreApi.projects.settings.get(options.projectId).pipe(
          map(settings => this.renameApplications(settings.viewer_apps || [])),
          catchError(() => of([])),
          shareReplay(1),
        );
        this.projectViewerApps.set(options.projectId, projectApps$);
      }
    }
    return combineLatest([projectApps$, this.viewerApps$]).pipe(
      map(([projectApps, siteApps]) => [...projectApps, ...siteApps]),
    );
  }

  public resetProjectApps(): void {
    this.projectViewerApps.clear();
  }

  public isAnyOhifViewerEnabled(): boolean {
    return this.configService.isFeatureEnabled('ohif_v3') || this.configService.isFeatureEnabled('ohif_react') || this.configService.isFeatureEnabled('ohif_meteor');
  }

  public ohifViewerDialog(config: OhifLaunchConfig): ViewerResult {
    if (config.app === OhifApp.OhifViewerV3 ||
      (this.configService.isFeatureEnabled('ohif_v3_default_app') && !config.app)) {
      config.app = OhifApp.OhifViewerV3;
      // OHIF viewer v3 is currently only launched as a new tab, don't persist the url
      const url = buildOhifUrl(config);
      window.open(url, '_blank');
      return of(undefined);
    }

    if (this.configService.isFeatureEnabled('url_access_apps')) {
      const viewerParams = objectToUrlParam({
        ohif: config,
      });
      this.persistedViewerOpen = true;
      this.routingService.setQueryParams({ viewerParams });
      const routeSettle$ = this.waitForRouteSettle().pipe(shareReplay(1));
      const result = routeSettle$.pipe(
        switchMap(() => this.openOhifViewerDialog(config)),
        shareReplay(1),
      )
      // shareReplay and subscribe to ensure this works the same whether the caller subscribes or not
      result.subscribe();
      return result;
    }
    return this.openOhifViewerDialog(config);
  }

  private openOhifViewerDialog(config: OhifLaunchConfig): ViewerResult {
    return this.dialogService.open(OhifViewerDialog, {
      data: config,
      disableClose: true,
      height: '98vh',
      width: '98vw',
      panelClass: 'padding-0',
    }).afterClosed().pipe(
      tap(() => {
        if (this.configService.isFeatureEnabled('url_access_apps')) {
          // remove the viewerParams when the dialog closes
          this.routingService.setQueryParams({ viewerParams: null });
        }
      }),
    );
  }

  /**
   * Open the Monaco viewer to edit a string resource
   * @param textConfig The text to edit
   * @param saveCallback A callback for save logic which returns an Observable. This observable is subscribed to and the dialog displays a resulting notification.
   * @returns An observable that emits when the dialog closes (indepedent of save success)
   **/
  public editTextInMonacoViewer(textConfig: ViewerTextConfig, saveCallback: SaveObservableCallback): Observable<DialogResult> {
    const apps = findAssociatedApps(defaultViewerApps, { file: { type: 'source code' } as File });
    if (apps.length === 0) {
      throw new Error("Can't resolve code editor");
    }
    const saved = new Subject<void>();
    const dialog = this.dialogService.open(ViewerFrameDialog, {
      panelClass: 'padding-0',
      data: {
        applicationUrl: apps[0].url,
        text: textConfig,
        saveText: (text: string) => {
          saveCallback(text, dialog).subscribe({
            next: () => {
              saved.next();
            },
            error: (err: Error) => {
              this.logger.error(err);
              saved.error(err);
            },
          });
        },
        textSaved: saved.asObservable(),
      },
    });
    return dialog.afterClosed() as Observable<DialogResult>;
  }

  public editProtocolInMonacoViewer(protocol: Protocol, config: ProtocolViewerConfig, saveCallback: SaveObservableCallback): Observable<DialogResult> {
    const apps = findAssociatedApps(defaultViewerApps, { file: { type: 'source code' } as File });
    if (apps.length === 0) {
      throw new Error("Can't resolve code editor");
    }
    const dialog = this.dialogService.open(ViewerFrameDialog, {
      panelClass: 'padding-0',
      data: {
        applicationUrl: apps[0].url,
        text: {
          fileType: 'json',
          autoFormat: true,
        },
        saveText: (text: string) => {
          const result = new Subject<void>();
          saveCallback(text, dialog).subscribe({
            next: () => {
              result.next();
              result.complete();
            },
            error: (err: Error) => {
              this.logger.error(err);
              result.error(err);
            },
          });
          return result.asObservable();
        },
        width: '900px',
        params: { protocol, config },
      },
    });
    return dialog.afterClosed() as Observable<DialogResult>;
  }

  private filterEnabledApps(viewer: ViewerApp): boolean {
    if (!this.configService.isFeatureEnabled('ohif_meteor') && viewer.url === InternalViewerUrl.OhifMeteor) {
      return false;
    }
    if (!this.configService.isFeatureEnabled('ohif_react') && viewer.url === InternalViewerUrl.OhifReact) {
      return false;
    }
    if (!this.configService.isFeatureEnabled('ohif_v3') && viewer.url === InternalViewerUrl.OhifViewerV3) {
      return false;
    }
    return true;
  }

  private renameApplications(apps: ViewerApp[]): ViewerApp[] {
    return apps.map(app => {
      if (/^internal:/.test(app.url) || /\/cdn\.flywheel\.io\//.test(app.url)) {
        switch (app.name.toLowerCase()) {
          case 'image viewer': return { ...app, name: 'Lite Viewer' };
          case 'ohif viewer': return { ...app, name: 'Viewer' };
          case 'legacy ohif viewer': return { ...app, name: 'Legacy Viewer' };
        }
      }
      return app;
    });
  }

  public view(options: ViewerConfig): ViewerResult {
    const { app, file, container, params, text } = options;
    const projectId = getContainerProjectId(container);
    const persistToUrl = this.configService.isFeatureEnabled('url_access_apps');
    // The attachmentUrl is used for a few different viewers, make it available to all
    return (file ? this.getAttachmentUrl(container, file, true) : of(null)).pipe(switchMap(attachmentUrl => {
      // request config is needed for the meteor viewer, which can be launched as an extension app
      const requestConfig = this.getRequestConfig(options);
      if (app) {
        // if app is provided from options, assume a user selected viewer
        // OHIF viewer v3 is currently only launched as a new tab, don't persist the url
        if (persistToUrl && app.url !== InternalViewerUrl.OhifViewerV3) {
          const viewerParams = objectToUrlParam({
            app: app.id,
            container: container._id,
            file: file?.file_id,
            params,
          });
          this.persistedViewerOpen = true;
          this.routingService.setQueryParams({ viewerParams });
          return this.waitForRouteSettle().pipe(
            switchMap(() => {
              return this.viewInExtensionApp({
                app,
                attachmentUrl,
                file,
                container,
                requestConfig,
                params,
                text,
              });
            })
          );
        }
        return this.viewInExtensionApp({
          app,
          attachmentUrl,
          file,
          container,
          requestConfig,
          params,
          text,
        }).toPromise();
      }
      // If we've gotten here, the calling function has not specified a viewer
      return this.getAssociatedApps(file ? { file, projectId } : { container, projectId }).
        pipe(switchMap((viewerApps) => {
          if (viewerApps[0]) {
            let defaultViewer = viewerApps[0];
            if (this.configService.isFeatureEnabled('ohif_v3_default_app')) {
              for (const app of viewerApps) {
                if (app.url === InternalViewerUrl.OhifViewerV3) {
                  defaultViewer = app;
                  break;
                }
              }
            }
            // OHIF viewer v3 is currently only launched as a new tab, don't persist the url
            if (persistToUrl && defaultViewer.url !== InternalViewerUrl.OhifViewerV3) {
              const viewerParams = objectToUrlParam({
                app: defaultViewer.id,
                container: container._id,
                file: file?.file_id,
                params,
              });
              this.persistedViewerOpen = true;
              this.routingService.setQueryParams({ viewerParams });
              return this.waitForRouteSettle().pipe(
                switchMap(() => {
                  return this.viewInExtensionApp({
                    attachmentUrl,
                    app: defaultViewer,
                    file,
                    container,
                    requestConfig,
                    params,
                    text,
                  });
                })
              );
            }
            return this.viewInExtensionApp({
              attachmentUrl,
              app: defaultViewer,
              file,
              container,
              requestConfig,
              params,
              text,
            });
          }
          // DEPRECATED: I haven't seen any cases which call this, so I didn't include persistent URL support
          // if file is not provided assumes execution on container
          const viewerType = getViewerType(file);
          const baseViewer: ViewerFunction = this.getViewer(viewerType) || (() => {
            const error = `No viewer found for ${viewerType}`;
            this.logger.error(error);
            return throwError(new Error(error));
          });
          return baseViewer({ attachmentUrl, file, requestConfig, ...options } as ViewerOptions)
            .pipe(
              catchError(error => {
                this.logger.error(viewerType + ' failed to load');
                throw new Error(error);
              }));
        }));
    }));
  }

  public async restoreViewerState(state: string): Promise<void> {
    const persistToUrl = this.configService.isFeatureEnabled('url_access_apps');
    if (!persistToUrl) {
      return;
    }
    const viewerState = urlParamToObject(state);
    const viewerOptions: ViewerLaunchConfig = {};
    if (viewerState.container) {
      const container = await firstValueFrom(this.coreApi.containers.get(viewerState.container)) as Container;
      viewerOptions.container = container;
    }
    if (viewerState.file) {
      const file = await firstValueFrom(this.coreApi.files.get(viewerState.file));
      viewerOptions.file = file;
    }
    if (viewerState.app) {
      const app = (await firstValueFrom(this.getAllApps({
        file: viewerOptions.file,
        container: viewerOptions.container,
        projectId: getContainerProjectId(viewerOptions.container),
      }))).find(app => app.id === viewerState.app);
      if (app){
        viewerOptions.app = app;
      }
    }
    if (viewerState.ohif) {
      this.ohifViewerDialog(viewerState.ohif);
      return;
    }
    if (!viewerState.app || !viewerOptions.container) {
      this.notifications.error('viewerService.restore.error');
      return;
    }
    if (viewerState.params) {
      viewerOptions.params = pick(viewerState.params, ['fileIds']);
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this.view(viewerOptions).subscribe(() => {});
  }

  private viewInExtensionApp(cfg: ViewerOptions): ViewerResult {
    const params = Object.assign({}, cfg.app.options, cfg.params); // merge static options and runtime params together
    const projectId = getContainerProjectId(cfg.container);
    const sessionId = cfg?.container?.session;
    const acquisitionIds = sessionId ? [cfg.container._id] : null;
    const fileId = cfg?.file?._id;
    const fileIds = fileId ? [fileId] : null;
    if (cfg.app.url === InternalViewerUrl.OhifReact) {
      const containerId = cfg?.container?._id;
      const analysisId = cfg?.container?.analysis;
      if (cfg.file.type === FileType.nifti) {
        return this.openOhifViewerDialog({ containerId, projectId, niftiFile: cfg.file, analysis: analysisId });
      }
      if (cfg.file.type === FileType.image) {
        return this.openOhifViewerDialog({ containerId, projectId, imageFile: cfg.file });
      }
      if (cfg.file.type === FileType.metaio) {
        return this.openOhifViewerDialog({ containerId, projectId, metaioFile: cfg.file });
      }
      return this.openOhifViewerDialog({ projectId, sessionId, acquisitionIds, fileIds, analysis: analysisId });
    }
    if (cfg.app.url === InternalViewerUrl.OhifMeteor) {
      return this.viewDicomsMeteor(cfg);
    }
    if (cfg.app.url === InternalViewerUrl.OhifViewerV3) {
      const selectedFileId = cfg.file.file_id;
      const config: OhifLaunchConfig = {
        app: OhifApp.OhifViewerV3,
        projectId,
        sessionId,
        acquisitionIds,
        fileIds,
        fileId: selectedFileId,
      };
      const url = buildOhifUrl(config);
      window.open(url, '_blank');
      return of(undefined);
    }

    return this.dialogService.open(ViewerFrameDialog, {
      panelClass: 'padding-0',
      data: {
        applicationUrl: cfg.app.url,
        container: cfg.container,
        file: cfg.file,
        params: params,
        text: cfg.text,
      },
    }).afterClosed().pipe(
      tap(() => {
        if (this.configService.isFeatureEnabled('url_access_apps')){
          // remove the viewerParams when the dialog closes
          this.routingService.setQueryParams({ viewerParams: null });
        }
      }),
    );
  }

  private viewInOHIFViewer(cfg: ViewerOptions): ViewerResult {
    if (cfg.requestConfig?.params) {
      const { fileId, containerId, fileName } = cfg.requestConfig.params;
      return this.containerAttachments.metadata({ fileId, containerId, fileName }).pipe(
        switchMap(info => {
          return this.openOHIFViewer(cfg, info);
        }),
        catchError(error => {
          this.logger.error(error);
          // if there is an error retrieving the info, open the viewer anyway
          return this.openOHIFViewer(cfg, null);
        })
      );
    }
    // request params are missing, open the viewer without the info
    return this.openOHIFViewer(cfg, null);
  }

  private viewDicomsMeteor(cfg: ViewerOptions): ViewerResult {
    if (getLastExtension(cfg.file.name) === 'zip') {
      return this.getArchiveMemberUrls(cfg).pipe(switchMap(memberUrls => {
        cfg = Object.assign({}, cfg, { attachmentUrl: memberUrls });
        return this.viewInOHIFViewer(cfg);
      }));
    }
    return this.viewInOHIFViewer(cfg);
  }

  private openOHIFViewer(cfg: ViewerOptions, info: File): ViewerResult {
    return this.profileService.getProfile().pipe(switchMap(profile => {
      const ohifProfile = Object.assign({ parentPermission: null }, profile);
      const parent = cfg.container;
      if (parent) {
        const permissions = parent.permissions || [];
        const userPermission = permissions.find(permission => permission._id === profile._id) as AccessPermission;
        ohifProfile.parentPermission = userPermission ? userPermission.access : '';
      }
      window.localStorage.removeItem('flywheelOHIFViewerData');
      return this.dialogService.open(LegacyOhifViewerDialog, {
        panelClass: 'padding-0',
        data: {
          requestConfig: cfg.requestConfig,
          format: cfg.file.type.toLowerCase(),
          images: Array.isArray(cfg.attachmentUrl) ? cfg.attachmentUrl : [cfg.attachmentUrl],
          info: info,
          parent: parent,
          profile: ohifProfile,
          title: cfg.file.name,
        },
      }).afterClosed().pipe(
        tap(() => {
          if (this.configService.isFeatureEnabled('url_access_apps')){
            // remove the viewerParams when the dialog closes
            this.routingService.setQueryParams({ viewerParams: null });
          }
        }),
      );
    }));
  }

  private getArchiveMemberUrls(cfg: ViewerOptions): Observable<string[]> {
    const { params } = deconstructUrl(cfg.attachmentUrl);
    const options = { ...getFileIdentifier(cfg.container, cfg.file), ticket: params.ticket };
    return this.containerAttachments.zipInfo(options).pipe(
      switchMap(zipInfo => {
        const urls$ = (zipInfo.members || [])
          .filter(file => file.size > 128) // core-api sometimes includes invalid 128b files
          .map(file => this.containerAttachments.zipMemberUrl(options, file.path));
        return urls$.length > 0 ? forkJoin(urls$) : of([]);
      }),
    );
  }

  private getRequestConfig(options: ViewerConfig): RequestConfig {
    const containerId = options.container._id;
    const parentId = options.container.parent?.id;
    const containerType = getContainerType(options.container);
    const fileName = options.file && options.file.name;
    const fileId = options.file && options.file.file_id;

    const config = {
      acquisition: {
        container: 'acquisitions',
        params: { containerId, fileName, fileId },
      },
      analysis: {
        container: 'analyses',
        params: { parentId, containerId, fileName, fileId },
      },
      collection: {
        container: 'collections',
        params: { containerId, fileName, fileId },
      },
      project: {
        container: 'projects',
        params: { containerId, fileName, fileId },
      },
      session: {
        container: 'sessions',
        params: { containerId, fileName, fileId },
      },
      subject: {
        container: 'subjects',
        params: { containerId, fileName, fileId },
      },
    };
    return config[containerType];
  }

  private getAttachmentUrl(container: Container, file: File, view = false): Observable<string> {
    return this.containerAttachments.downloadUrl(getFileIdentifier(container, file), view);
  }


  // @ts-expect-error need to fix this, not all paths return
  private getViewer(type: FileType): ViewerFunction {
    switch (type) {
      case FileType.image:
        return (cfg: ViewerOptions) => {
          const extension = getLastExtension(cfg.file.name);
          if (extension === 'zip') {
            return this.viewDicomsMeteor(cfg);
          }
          if (extension === 'tiff' || extension === 'tif') {
            cfg = Object.assign({}, cfg, { attachmentUrl: [cfg.attachmentUrl] });
            return this.viewInOHIFViewer(cfg);
          }
          return of(null);
        };
      case FileType.nifti:
        return (cfg: ViewerOptions) => {
          if (this.configService.isFeatureEnabled('ohif_react') || this.configService.isFeatureEnabled('ohif_v3')) {
            const projectId = getContainerProjectId(cfg.container);
            const containerId = cfg?.container?._id;
            return this.ohifViewerDialog({ containerId, projectId, niftiFile: cfg.file });
          }
          return this.viewInOHIFViewer(cfg);
        };
      case FileType.text:
      case FileType.metaio:
        return (cfg: ViewerOptions) => {
          const { url, params } = deconstructUrl(cfg.attachmentUrl);
          return this.dialogService.open(TextViewerDialog, {
            panelClass: 'padding-0',
            data: {
              title: cfg.file.name,
              text$: this.coreApi.get(url, params, { responseType: 'text' }),
            },
          }).afterClosed();
        };
      case FileType.pdf:
        return (cfg: ViewerOptions) => {
          return this.dialogService.open(PDFViewerDialog, {
            panelClass: 'padding-0',
            data: {
              title: cfg.file.name,
              pdf: cfg.attachmentUrl + '&view=true#view=fitH',
            },
          }).afterClosed();
        };
      case FileType.embeddedhtml:
        return (cfg: ViewerOptions) => {
          const { params } = deconstructUrl(cfg.attachmentUrl);
          const fileIdentifier = getFileIdentifier(cfg.container, cfg.file);
          return forkJoin([
            this.containerAttachments.zipMember(fileIdentifier, 'index.html').pipe(switchMap(blob => blob.text())),
            this.containerAttachments.zipInfo(fileIdentifier),
          ]).pipe(
            switchMap(async ([indexHtml, memberResponse]) => {
              for (const member of memberResponse.members) {
                // replace relative file link URLs with resolvable zip member URLs
                const regex = new RegExp(`=['"]([^'"]*${escapeRegex(member.path)})['"]`, 'g');
                const memberUrl = await firstValueFrom(this.containerAttachments.zipMemberUrl({ ...fileIdentifier, ticket: params.ticket }, member.path));
                indexHtml = indexHtml.replace(regex, `="${memberUrl}"`);
              }
              return this.dialogService.open(HTMLViewerDialog, {
                panelClass: 'padding-0',
                data: {
                  attachmentUrl: cfg.attachmentUrl,
                  title: cfg.file.name,
                  html: indexHtml,
                },
              }).afterClosed();
            }),
          );
        };
      case FileType.markdown:
        return (cfg: ViewerOptions) => {
          const { url, params } = deconstructUrl(cfg.attachmentUrl);
          return this.dialogService.open(HTMLViewerDialog, {
            panelClass: 'padding-0',
            data: {
              title: cfg.file.name,
              html$: this.coreApi.get(url, params, { responseType: 'text' }).pipe(map((text: string) => this.markdownService.render(text))),
            },
          }).afterClosed();
        };
      case FileType.markup:
        return (cfg: ViewerOptions) => {
          return this.dialogService.open(HTMLViewerDialog, {
            panelClass: 'padding-0',
            data: {
              title: cfg.file.name,
              iframeSrc: cfg.attachmentUrl + '&view=true',
            },
          }).afterClosed();
        };
      case FileType.dicom:
        return (cfg: ViewerOptions) => {
          if (this.configService.isFeatureEnabled('ohif_react') || this.configService.isFeatureEnabled('ohif_v3')) {
            const projectId = getContainerProjectId(cfg.container);
            const sessionId = cfg.container.session;
            const acquisitionIds = sessionId ? [cfg.container._id] : null;
            const fileIds = cfg.file?._id ? [cfg.file._id] : null;
            return this.ohifViewerDialog({ projectId, sessionId, acquisitionIds, fileIds });
          }
          return this.viewDicomsMeteor(cfg);
        };
      case FileType.video:
        return (cfg: ViewerOptions) => {
          return this.dialogService.open(VideoViewerDialog, {
            panelClass: 'padding-0',
            data: {
              title: cfg.file.name,
              video: {
                src: cfg.attachmentUrl + '&view=true',
              },
            },
          }).afterClosed();
        };
      default: of(null);
    }
  }

  private waitForRouteSettle(): Observable<void> {
    const location$ = new Observable<PopStateEvent>(subscriber => {
      return this.location.subscribe(event => {
        subscriber.next(event);
      });
    });
    return location$.pipe(
      distinctUntilKeyChanged('url'),
      startWith(null),
      debounceTime(50),
      first(),
      map(() => null),
    );
  }
}
